// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import { Text } from '@/components/Themed';
import { groupItems } from '@/features/game-definitions/groups';
import type { GameDefinition } from '@/features/game-definitions/types';
import appColors from '@/theme/colors';

const FOUR_COLUMN_BREAKPOINT = 1500;
const THREE_COLUMN_BREAKPOINT = 900;
const GAP = 12;

type Props = {
  games: GameDefinition[];
  /** Renders one tile. Must set a `key` — rows are plain arrays. */
  renderTile: (game: GameDefinition) => ReactNode;
};

/**
 * Responsive game grid bucketed into the shared visual groups (main / match / team /
 * hazard / achievement / challenge / tournament). Rows are padded with fillers so a
 * short final row keeps the same column widths as a full one.
 */
export default function GroupedGameGrid({ games, renderTile }: Props) {
  const { width: viewportWidth } = useWindowDimensions();

  const numColumns = useMemo(() => {
    if (viewportWidth >= FOUR_COLUMN_BREAKPOINT) return 4;
    if (viewportWidth >= THREE_COLUMN_BREAKPOINT) return 3;
    return 2;
  }, [viewportWidth]);

  const groups = useMemo(
    () => groupItems(games, (game) => game.id, (game) => game.name),
    [games],
  );

  const groupRows = useMemo(
    () =>
      groups.map((group) => {
        const rows: GameDefinition[][] = [];
        for (let i = 0; i < group.items.length; i += numColumns) {
          rows.push(group.items.slice(i, i + numColumns));
        }
        return { ...group, rows };
      }),
    [groups, numColumns],
  );

  return (
    <View style={styles.grid}>
      {groupRows.map((group) => (
        <View key={group.groupId} style={styles.group}>
          <Text style={styles.groupHeader}>{group.title}</Text>
          {group.rows.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.row}>
              {row.map(renderTile)}
              {Array.from({ length: numColumns - row.length }, (_, i) => (
                <View key={`filler-${i}`} style={styles.filler} />
              ))}
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    gap: 16,
  },
  group: {
    gap: GAP,
  },
  groupHeader: {
    fontSize: 15,
    fontWeight: '700',
    color: appColors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  row: {
    flexDirection: 'row',
    gap: GAP,
    alignItems: 'stretch',
  },
  filler: {
    flex: 1,
  },
});
